import { useCallback, useMemo, useState } from 'react';
import { intervalOptions } from '~/utils';
import useSearchParamsConfig from './useSearchParamsConfig';

export default function useTaskFilters(defaultSearchParams) {
  const filters = useMemo(
    () => ({
      projectTasks: [],
      projectTaskStatuses: [],
      projectTaskRecordStatusId: null,
      projectTaskTags: [],
      projectTaskStartDatePeriod: null,
      projectTaskEndDatePeriod: null,
    }),
    [],
  );

  const [defaultParams] = useState(defaultSearchParams);

  const searchParamsConfig = useSearchParamsConfig();

  const taskSearchParamsConfig = useMemo(
    () => ({
      projectTasks: searchParamsConfig.projectTasks,
      projectTaskStatuses: searchParamsConfig.projectTaskStatuses,
      projectTaskRecordStatusId: searchParamsConfig.projectTaskRecordStatusId,
      projectTaskTags: searchParamsConfig.projectTaskTags,
      projectTaskStartDatePeriod: {
        ...searchParamsConfig.period,
        default: intervalOptions.all_dates,
        keys: ['projectTaskStartPeriod', 'projectTaskStartFrom', 'projectTaskStartTo'],
      },
      projectTaskEndDatePeriod: {
        ...searchParamsConfig.period,
        default: intervalOptions.all_dates,
        keys: ['projectTaskEndPeriod', 'projectTaskEndFrom', 'projectTaskEndTo'],
      },
      ...defaultParams,
    }),
    [searchParamsConfig, defaultParams],
  );

  const mapUrlSearchParams = useCallback(
    (filters) => ({
      projectTaskId: filters.projectTasks?.map((v) => v.id),
      projectTaskStatusId: filters.projectTaskStatuses?.map((v) => v.id),
      projectTaskRecordStatusId: filters.projectTaskRecordStatusId ?? undefined,
      projectTaskTagId: filters.projectTaskTags?.map((v) => v.id),
      projectTaskStartFrom: filters.projectTaskStartDatePeriod?.start ?? undefined,
      projectTaskStartTo: filters.projectTaskStartDatePeriod?.end ?? undefined,
      projectTaskEndFrom: filters.projectTaskEndDatePeriod?.start ?? undefined,
      projectTaskEndTo: filters.projectTaskEndDatePeriod?.end ?? undefined,
    }),
    [],
  );

  const mapClickThroughParams = useCallback(
    (filters) => ({
      projectTaskId: filters.projectTasks?.map((v) => v.id),
      projectTaskStatusId: filters.projectTaskStatuses?.map((v) => v.id),
      projectTaskRecordStatusId: filters.projectTaskRecordStatusId ?? undefined,
      projectTaskTagId: filters.projectTaskTags?.map((v) => v.id),
      projectTaskStartFrom: filters.projectTaskStartDatePeriod?.start ?? undefined,
      projectTaskStartTo: filters.projectTaskStartDatePeriod?.end ?? undefined,
      projectTaskEndFrom: filters.projectTaskEndDatePeriod?.start ?? undefined,
      projectTaskEndTo: filters.projectTaskEndDatePeriod?.end ?? undefined,
    }),
    [],
  );

  return useMemo(
    () => ({
      filters,
      searchParamsConfig: taskSearchParamsConfig,
      mapUrlSearchParams,
      mapClickThroughParams,
    }),
    [filters, taskSearchParamsConfig, mapUrlSearchParams, mapClickThroughParams],
  );
}
